<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="confirmState"
        class="fixed inset-0 z-[200] flex items-center justify-center p-4"
        @click.self="close(false)">

        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm"/>

        <!-- Dialog -->
        <div class="relative bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-4">

          <!-- Alert icon -->
          <div v-if="confirmState.isAlert" class="flex items-center gap-3">
            <div class="shrink-0 w-9 h-9 rounded-full bg-amber-500/15 flex items-center justify-center">
              <svg class="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              </svg>
            </div>
            <h3 class="text-sm font-semibold text-white leading-snug">{{ confirmState.title }}</h3>
          </div>
          <h3 v-else class="text-sm font-semibold text-white leading-snug">{{ confirmState.title }}</h3>

          <p v-if="confirmState.message" class="text-xs text-gray-300 leading-relaxed">
            {{ confirmState.message }}
          </p>

          <!-- Hint box (shown only for alerts) -->
          <div v-if="confirmState.isAlert && confirmState.hint"
            class="bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-xs text-gray-400 leading-relaxed">
            {{ confirmState.hint }}
          </div>

          <div class="flex items-center justify-end gap-2 pt-1">
            <button
              v-if="confirmState.cancelText"
              @click="close(false)"
              class="px-4 py-1.5 rounded-lg text-xs font-medium text-gray-400 hover:text-white hover:bg-gray-800 transition">
              {{ confirmState.cancelText }}
            </button>
            <button
              ref="confirmBtn"
              @click="close(true)"
              :class="['px-4 py-1.5 rounded-lg text-xs font-semibold transition',
                confirmState.isAlert
                  ? 'bg-amber-500 hover:bg-amber-400 text-gray-900'
                  : confirmState.danger
                    ? 'bg-red-600 hover:bg-red-500 text-white'
                    : 'bg-white hover:bg-gray-100 text-gray-900']">
              {{ confirmState.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, watch, nextTick, onUnmounted } from 'vue'
import { useConfirm } from '@/composables/useConfirm'

const { confirmState, close } = useConfirm()
const confirmBtn = ref(null)

const onKey = (e) => {
  if (!confirmState.value) return
  if (e.key === 'Escape') { e.preventDefault(); close(false) }
  if (e.key === 'Enter')  { e.preventDefault(); close(true)  }
}

watch(confirmState, async (v) => {
  if (v) {
    await nextTick()
    confirmBtn.value?.focus()
    window.addEventListener('keydown', onKey)
  } else {
    window.removeEventListener('keydown', onKey)
  }
})

onUnmounted(() => window.removeEventListener('keydown', onKey))
</script>

<style scoped>
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.15s ease;
}
.modal-enter-active .relative, .modal-leave-active .relative {
  transition: transform 0.15s ease, opacity 0.15s ease;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
}
.modal-enter-from .relative {
  transform: scale(0.95) translateY(8px);
  opacity: 0;
}
</style>
